/**
 * ProductDetailPage Component
 * 
 * Página de detalhes do produto com informações completas.
 * Refatorado para TypeScript com design moderno e gradientes coloridos.
 * 
 * Funcionalidades:
 * - Visualização completa de informações do produto
 * - Edição (se usuário tiver acesso)
 * - Cálculos automáticos de markup, margem, preço mínimo
 * - Geração de código de barras
 * - Modal de zoom para imagens
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  X, 
  Package, 
  DollarSign,
  Percent,
  Barcode,
  Ruler,
  Image as ImageIcon,
  History,
  TrendingUp,
  ShoppingCart,
  Package2,
  ShoppingBag,
  Info,
  ShieldCheck,
  Zap,
  Tag,
  Layers,
  ChevronDown,
  Link2
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions';
import { productService, productGroupService, brandService, unitService } from '../../../../services/erpApi';
import Modal from '../../../../components/ERP/Modal/Modal';
import SearchSelectModal from '../../../../components/ERP/SearchSelectModal/SearchSelectModal';
import ProductGridManager from '../../../../components/ERP/ProductGrid/ProductGridManager';
import ProductVariantsManager from '../../../../components/ERP/ProductGrid/ProductVariantsManager';
import ProductImageManagerComponent from '../../../../components/ProductImageManager/ProductImageManager';
import { adminService } from '../../../../services/api';
import { copyStorefrontProductUrl } from '../../../../utils/storefrontProductUrl';
import './ProductDetailPage.css';

const ProductImageManager = ProductImageManagerComponent as React.FC<{
  productId: number;
  productLabel?: string;
  editable?: boolean;
  onImagesChange?: () => void | Promise<void>;
}>;

// ============================================
// INTERFACES TYPESCRIPT
// ============================================

interface Product {
  id: number;
  sku?: string;
  name: string;
  description?: string;
  ecommerce_description?: string;
  cost_price?: number;
  price: number;
  barcode?: string;
  brand?: string;
  brand_id?: number;
  brand_name?: string;
  group_id?: number;
  group_name?: string;
  unit_id?: number;
  unit_name?: string;
  unit_code?: string;
  active: boolean;
  is_featured?: boolean;
  stock_quantity?: number;
  min_stock?: number;
  width?: number;
  height?: number;
  length?: number;
  weight?: number;
  image_url?: string;
  grid_id?: number;
  grid_name?: string;
  references?: string[];
}

interface ProductImage {
  id: number;
  url: string;
  isPrimary: boolean;
}

interface ProductGroup {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

interface Unit {
  id: number;
  name: string;
  code?: string;
}

interface FormData {
  sku: string;
  name: string;
  active: boolean;
  groupId: string;
  brandId: string;
  costPrice: string;
  price: string;
  unitId: string;
  barcode: string;
  minPrice: string;
  minStock: string;
  width: string;
  height: string;
  length: string;
  weight: string;
  description: string;
  ecommerceDescription: string;
  referencesText: string;
}

interface Calculations {
  markup: number;
  margin: number;
  minPrice: number;
  minMarkup: number;
  minMargin: number;
}

interface HistoryModals {
  prices: boolean;
  sales: boolean;
  quantities: boolean;
  purchases: boolean;
}

interface HistoryDataItem {
  data: any[];
  loading: boolean;
  error: string | null;
  statistics?: any;
}

interface HistoryData {
  prices: HistoryDataItem;
  sales: HistoryDataItem;
  quantities: HistoryDataItem;
  purchases: HistoryDataItem;
}

interface Errors {
  [key: string]: string;
}

type LookupModalType = 'group' | 'brand' | 'unit' | null;

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { hasPermission } = usePermissions();

  // ============================================
  // ESTADOS
  // ============================================
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [descriptionSaving, setDescriptionSaving] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [grupos, setGrupos] = useState<ProductGroup[]>([]);
  const [marcas, setMarcas] = useState<Brand[]>([]);
  const [unidades, setUnidades] = useState<Unit[]>([]);
  const [lookupModal, setLookupModal] = useState<LookupModalType>(null);
  const [lookupSearchTerm, setLookupSearchTerm] = useState('');
  const [formData, setFormData] = useState<FormData>({
    sku: '',
    name: '',
    active: true,
    groupId: '',
    brandId: '',
    costPrice: '',
    price: '',
    unitId: '',
    barcode: '',
    minPrice: '',
    minStock: '',
    width: '',
    height: '',
    length: '',
    weight: '',
    description: '',
    ecommerceDescription: '',
    referencesText: ''
  });
  const [images, setImages] = useState<ProductImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<
    'dados' | 'fotos' | 'grade' | 'descricao' | 'historico'
  >('dados');
  const [contentSubTab, setContentSubTab] = useState<'descricao' | 'especificacoes'>(
    'descricao'
  );
  const [historyModals, setHistoryModals] = useState<HistoryModals>({
    prices: false,
    sales: false,
    quantities: false,
    purchases: false
  });
  const [historyData, setHistoryData] = useState<HistoryData>({
    prices: { data: [], loading: false, error: null },
    sales: { data: [], loading: false, error: null, statistics: null },
    quantities: { data: [], loading: false, error: null, statistics: null },
    purchases: { data: [], loading: false, error: null, statistics: null }
  });
  const [errors, setErrors] = useState<Errors>({});
  const [calculations, setCalculations] = useState<Calculations>({
    markup: 0,
    margin: 0,
    minPrice: 0,
    minMarkup: 0,
    minMargin: 0
  });

  // ============================================
  // FUNÇÕES DE CARREGAMENTO
  // ============================================

  const loadProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await productService.getById(id);
      if (response.success) {
        const produto = response.data;
        setProduct(produto);
        setFormData({
          sku: produto.sku || '',
          name: produto.name || '',
          active: produto.active !== undefined ? produto.active : true,
          groupId: produto.group_id?.toString() || '',
          brandId: produto.brand_id?.toString() || '',
          costPrice: produto.cost_price?.toString() || '',
          price: produto.price?.toString() || '',
          unitId: produto.unit_id?.toString() || '',
          barcode: produto.barcode || '',
          minPrice: '',
          minStock: produto.min_stock?.toString() || '',
          width: produto.width?.toString() || '',
          height: produto.height?.toString() || '',
          length: produto.length?.toString() || '',
          weight: produto.weight?.toString() || '',
          description: produto.description || '',
          ecommerceDescription: produto.ecommerce_description || '',
          referencesText: (produto.references || []).join('\n')
        });
        
        // Carregar imagens do produto
        try {
          const imagesResponse = await adminService.getProductImages(id);
          if (imagesResponse.success && imagesResponse.data && imagesResponse.data.length > 0) {
            const formattedImages = imagesResponse.data.map((img: any) => ({
              id: img.id,
              url: img.image_url,
              isPrimary: img.display_order === 0
            }));
            setImages(formattedImages);
            if (formattedImages.length > 0) {
              setSelectedImageIndex(0);
            }
          } else {
            if (produto.image_url) {
              setImages([{ id: 1, url: produto.image_url, isPrimary: true }]);
              setSelectedImageIndex(0);
            } else {
              setImages([]);
            }
          }
        } catch (error) {
          console.error('Erro ao carregar imagens:', error);
          if (produto.image_url) {
            setImages([{ id: 1, url: produto.image_url, isPrimary: true }]);
            setSelectedImageIndex(0);
          } else {
            setImages([]);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadOptions = useCallback(async () => {
    try {
      const [gruposRes, marcasRes, unidadesRes] = await Promise.all([
        productGroupService.getAll({ active: true }),
        brandService.getAll({ active: true }),
        unitService.getAll({ active: true })
      ]);

      if (gruposRes.success) setGrupos(gruposRes.data || []);
      if (marcasRes.success) setMarcas(marcasRes.data || []);
      if (unidadesRes.success) setUnidades(unidadesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  }, []);

  const openLookupModal = (type: Exclude<LookupModalType, null>) => {
    setLookupSearchTerm('');
    setLookupModal(type);
  };

  const closeLookupModal = () => {
    setLookupModal(null);
    setLookupSearchTerm('');
  };

  const selectedGroupLabel = useMemo(() => {
    if (!formData.groupId) return 'Escolha um grupo (opcional)';
    const group = grupos.find((g) => String(g.id) === String(formData.groupId));
    return group?.name || product?.group_name || 'Grupo selecionado';
  }, [formData.groupId, grupos, product?.group_name]);

  const selectedBrandLabel = useMemo(() => {
    if (!formData.brandId) return 'Escolha uma marca (opcional)';
    const brand = marcas.find((b) => String(b.id) === String(formData.brandId));
    return brand?.name || product?.brand_name || 'Marca selecionada';
  }, [formData.brandId, marcas, product?.brand_name]);

  const selectedUnitLabel = useMemo(() => {
    if (!formData.unitId) return 'Escolha uma unidade';
    const unit = unidades.find((u) => String(u.id) === String(formData.unitId));
    if (unit) {
      return unit.code ? `${unit.name} — ${unit.code}` : unit.name;
    }
    if (product?.unit_name) {
      return product.unit_code
        ? `${product.unit_name} — ${product.unit_code}`
        : product.unit_name;
    }
    return 'Unidade selecionada';
  }, [formData.unitId, unidades, product?.unit_name, product?.unit_code]);

  const groupItems = useMemo(
    () => grupos.map((g) => ({ id: g.id, name: g.name })),
    [grupos]
  );

  const brandItems = useMemo(
    () => marcas.map((b) => ({ id: b.id, name: b.name })),
    [marcas]
  );

  const unitItems = useMemo(
    () =>
      unidades.map((u) => ({
        id: u.id,
        name: u.name,
        detail: u.code || undefined,
      })),
    [unidades]
  );

  // ============================================
  // FUNÇÕES DE CÁLCULO
  // ============================================

  const calculateValues = useCallback(() => {
    const cost = parseFloat(formData.costPrice) || 0;
    const price = parseFloat(formData.price) || 0;
    const minPrice = parseFloat(formData.minPrice) || 0;

    const markup = cost > 0 ? ((price - cost) / cost) * 100 : 0;
    const margin = price > 0 ? ((price - cost) / price) * 100 : 0;
    const calcMinMarkup = cost > 0 && minPrice > 0 ? ((minPrice - cost) / cost) * 100 : 0;
    const calcMinMargin = minPrice > 0 ? ((minPrice - cost) / minPrice) * 100 : 0;

    setCalculations({
      markup,
      margin,
      minPrice,
      minMarkup: calcMinMarkup,
      minMargin: calcMinMargin
    });
  }, [formData.costPrice, formData.minPrice, formData.price]);

  const generateBarcode = () => {
    const prefix = '789';
    const random = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
    const code = prefix + random;
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(code[i]) * (i % 2 === 0 ? 1 : 3);
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    const barcode = code + checkDigit;
    
    setFormData({ ...formData, barcode });
  };

  // ============================================
  // FUNÇÕES DE FORMATAÇÃO
  // ============================================

  const formatCurrency = (value: number | null | undefined): string => {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    if (!value && value !== 0) return '0,00%';
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // ============================================
  // FUNÇÕES DE SALVAMENTO
  // ============================================

  const parseReferencesFromText = (text: string): string[] => {
    const refs = text
      .split(/[\n,;]+/)
      .map((ref) => ref.trim())
      .filter((ref) => ref.length > 0);
    return Array.from(new Set(refs));
  };

  const canEdit = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.UPDATE);
  const canEditDescription = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_DESCRIPTION);
  const canEditBrand = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_BRAND);
  const canEditGroup = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_GROUP);
  const canEditCubage = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_EDIT_CUBAGE);
  const hideProductCost = !isAdmin() && hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_HIDE_COST);
  const canOpenEditor = canEdit || canEditBrand || canEditGroup || canEditCubage;

  const handleSave = async () => {
    setErrors({});
    try {
      const restrictedFields = {
        ...(canEditGroup ? { groupId: formData.groupId || null } : {}),
        ...(canEditBrand ? { brandId: formData.brandId || null } : {}),
        ...(canEditCubage
          ? {
              width: formData.width ? parseFloat(formData.width) : null,
              height: formData.height ? parseFloat(formData.height) : null,
              length: formData.length ? parseFloat(formData.length) : null,
              weight: formData.weight ? parseFloat(formData.weight) : null
            }
          : {}),
        ...(canEditDescription
          ? {
              description: formData.description || null,
              ecommerceDescription: formData.ecommerceDescription || null
            }
          : {})
      };

      const dataToSend = {
        sku: formData.sku,
        name: formData.name,
        active: formData.active,
        ...(hideProductCost
          ? {}
          : { costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null }),
        price: parseFloat(formData.price),
        minStock: formData.minStock ? parseInt(formData.minStock) : null,
        unitId: formData.unitId || null,
        barcode: formData.barcode || null,
        ...restrictedFields
      };

      if (!canEdit) {
        const descriptionOnly = { ...restrictedFields };
        const response = await productService.update(id!, descriptionOnly);
        if (!response.success) {
          setErrors({ submit: response.message || 'Erro ao salvar produto' });
          return;
        }
        setEditing(false);
        loadProduct();
        return;
      }

      const references = parseReferencesFromText(formData.referencesText);

      const response = await productService.update(id!, dataToSend);
      if (!response.success) {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj: Errors = {};
          response.errors.forEach((err: any) => {
            errorObj[err.param || err.path] = err.msg || err.message;
          });
          setErrors(errorObj);
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar produto' });
        }
        return;
      }

      const refsResponse = await productService.updateReferences(id!, references);
      if (refsResponse.success) {
        setEditing(false);
        loadProduct();
      } else {
        setErrors({ references: refsResponse.message || 'Erro ao salvar referências (cProd)' });
      }
    } catch (error: any) {
      console.error('Erro ao salvar produto:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao salvar produto';
      const errorDetails = error.response?.data?.errors;
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj: Errors = {};
        errorDetails.forEach((err: any) => {
          errorObj[err.param || err.path] = err.msg || err.message;
        });
        setErrors(errorObj);
      } else {
        setErrors({ submit: errorMessage });
      }
    }
  };

  const descriptionDirty = (formData.ecommerceDescription || '') !== (product?.ecommerce_description || '');

  const handleSaveDescription = async () => {
    if (!id || !canEditDescription || descriptionSaving) return;
    setDescriptionSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.ecommerceDescription;
      return next;
    });
    try {
      const response = await productService.update(id, {
        ecommerceDescription: formData.ecommerceDescription || null
      });
      if (!response.success) {
        setErrors((prev) => ({
          ...prev,
          ecommerceDescription: response.message || 'Erro ao salvar descrição'
        }));
        return;
      }
      setProduct((prev) => (
        prev
          ? { ...prev, ecommerce_description: formData.ecommerceDescription || '' }
          : prev
      ));
    } catch (error: any) {
      setErrors((prev) => ({
        ...prev,
        ecommerceDescription: error.response?.data?.message || 'Erro ao salvar descrição'
      }));
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleCopyEcommerceLink = async () => {
    if (!product?.id) return;
    try {
      await copyStorefrontProductUrl(product.id);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (error: any) {
      console.error('Erro ao copiar link do e-commerce:', error);
      setErrors({ submit: error?.message || 'Não foi possível copiar o link' });
    }
  };

  // ============================================
  // FUNÇÕES DE HISTÓRICO
  // ============================================

  const loadPriceHistory = useCallback(async () => {
    if (!id) return;
    setHistoryData(prev => ({ ...prev, prices: { ...prev.prices, loading: true, error: null } }));
    try {
      const response = await productService.getPriceHistory(id, { limit: 100 });
      if (response.success) {
        setHistoryData(prev => ({ ...prev, prices: { data: response.data, loading: false, error: null } }));
      } else {
        setHistoryData(prev => ({ ...prev, prices: { ...prev.prices, loading: false, error: response.message || 'Erro ao carregar histórico' } }));
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de preços:', error);
      setHistoryData(prev => ({ ...prev, prices: { ...prev.prices, loading: false, error: 'Erro ao carregar histórico de preços' } }));
    }
  }, [id]);

  const loadSalesHistory = useCallback(async () => {
    if (!id) return;
    setHistoryData(prev => ({ ...prev, sales: { ...prev.sales, loading: true, error: null } }));
    try {
      const response = await productService.getSalesHistory(id, { limit: 100 });
      if (response.success) {
        setHistoryData(prev => ({ ...prev, sales: { data: response.data, statistics: response.statistics, loading: false, error: null } }));
      } else {
        setHistoryData(prev => ({ ...prev, sales: { ...prev.sales, loading: false, error: response.message || 'Erro ao carregar histórico' } }));
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de vendas:', error);
      setHistoryData(prev => ({ ...prev, sales: { ...prev.sales, loading: false, error: 'Erro ao carregar histórico de vendas' } }));
    }
  }, [id]);

  const loadStockHistory = useCallback(async () => {
    if (!id) return;
    setHistoryData(prev => ({ ...prev, quantities: { ...prev.quantities, loading: true, error: null } }));
    try {
      const response = await productService.getStockHistory(id, { limit: 100 });
      if (response.success) {
        setHistoryData(prev => ({ ...prev, quantities: { data: response.data, statistics: response.statistics, loading: false, error: null } }));
      } else {
        setHistoryData(prev => ({ ...prev, quantities: { ...prev.quantities, loading: false, error: response.message || 'Erro ao carregar histórico' } }));
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de estoque:', error);
      setHistoryData(prev => ({ ...prev, quantities: { ...prev.quantities, loading: false, error: 'Erro ao carregar histórico de estoque' } }));
    }
  }, [id]);

  const loadPurchaseHistory = useCallback(async () => {
    if (!id) return;
    setHistoryData(prev => ({ ...prev, purchases: { ...prev.purchases, loading: true, error: null } }));
    try {
      const response = await productService.getPurchaseHistory(id, { limit: 100 });
      if (response.success) {
        setHistoryData(prev => ({ ...prev, purchases: { data: response.data, statistics: response.statistics, loading: false, error: null } }));
      } else {
        setHistoryData(prev => ({ ...prev, purchases: { ...prev.purchases, loading: false, error: response.message || 'Erro ao carregar histórico' } }));
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de compras:', error);
      setHistoryData(prev => ({ ...prev, purchases: { ...prev.purchases, loading: false, error: 'Erro ao carregar histórico de compras' } }));
    }
  }, [id]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (id) {
      loadProduct();
      loadOptions();
    }
  }, [id, loadProduct, loadOptions]);

  useEffect(() => {
    calculateValues();
  }, [calculateValues]);

  useEffect(() => {
    if (historyModals.prices) {
      loadPriceHistory();
    }
  }, [historyModals.prices, loadPriceHistory]);

  useEffect(() => {
    if (historyModals.sales) {
      loadSalesHistory();
    }
  }, [historyModals.sales, loadSalesHistory]);

  useEffect(() => {
    if (historyModals.quantities) {
      loadStockHistory();
    }
  }, [historyModals.quantities, loadStockHistory]);

  useEffect(() => {
    if (historyModals.purchases) {
      loadPurchaseHistory();
    }
  }, [historyModals.purchases, loadPurchaseHistory]);

  // ============================================
  // PERMISSÕES
  // ============================================

  const canManageImages = isAdmin() || hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCTS_IMAGES);

  // ============================================
  // RENDERIZAÇÃO
  // ============================================

  if (loading) {
    return (
      <div className="product-detail-page product-detail-page-modern">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando produto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page product-detail-page-modern">
        <div className="error-state">
          <p>Produto não encontrado</p>
          <button onClick={() => navigate('/erp/produtos/produtos')} className="btn-primary-modern">
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  const currentImage = images[selectedImageIndex]?.url || product.image_url || '';

  return (
    <div className="product-detail-page product-detail-page-modern">
      {/* Header com gradiente */}
      <div className="page-header-modern">
        <button 
          className="btn-back-modern"
          onClick={() => navigate('/erp/produtos/produtos')}
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
        <div className="header-actions-modern">
          <button
            type="button"
            className="btn-secondary-modern"
            onClick={handleCopyEcommerceLink}
            title="Copiar link para enviar ao cliente"
          >
            <Link2 size={18} />
            {linkCopied ? 'Copiado!' : 'Copiar link'}
          </button>
          {canOpenEditor && !editing && (
            <button 
              className="btn-primary-modern"
              onClick={() => setEditing(true)}
            >
              <Edit size={18} />
              Editar
            </button>
          )}
          {editing && (
            <>
              <button 
                className="btn-secondary-modern"
                onClick={() => {
                  setEditing(false);
                  loadProduct();
                }}
              >
                <X size={18} />
                Cancelar
              </button>
              <button 
                className="btn-primary-modern"
                onClick={handleSave}
              >
                <Save size={18} />
                Salvar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="product-detail-content-modern">
        <div className="product-page-tabs" role="tablist" aria-label="Seções do produto">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'dados'}
            className={`product-page-tab ${activeTab === 'dados' ? 'active' : ''}`}
            onClick={() => setActiveTab('dados')}
          >
            <Package size={16} aria-hidden />
            Dados
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'fotos'}
            className={`product-page-tab ${activeTab === 'fotos' ? 'active' : ''}`}
            onClick={() => setActiveTab('fotos')}
          >
            <ImageIcon size={16} aria-hidden />
            Fotos
            {images.length > 0 ? (
              <span className="product-page-tab-count">{images.length}</span>
            ) : null}
          </button>
          {canEdit ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'grade'}
              className={`product-page-tab ${activeTab === 'grade' ? 'active' : ''}`}
              onClick={() => setActiveTab('grade')}
            >
              <Layers size={16} aria-hidden />
              Grade
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'descricao'}
            className={`product-page-tab ${activeTab === 'descricao' ? 'active' : ''}`}
            onClick={() => setActiveTab('descricao')}
          >
            <Info size={16} aria-hidden />
            Descrição
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'historico'}
            className={`product-page-tab ${activeTab === 'historico' ? 'active' : ''}`}
            onClick={() => setActiveTab('historico')}
          >
            <History size={16} aria-hidden />
            Histórico
          </button>
        </div>

        {activeTab === 'dados' ? (
          <>
        {/* Seção: Informações Gerais */}
        <div className="detail-section-modern detail-section-gradient-1">
          <div className="section-header-with-image">
            <div className="product-avatar-container">
              {currentImage ? (
                <img 
                  src={currentImage} 
                  alt={product.name}
                  className="product-avatar-image"
                  onClick={() => setImageModalOpen(true)}
                  title="Clique para ampliar"
                />
              ) : (
                <div className="product-avatar-placeholder">
                  <Package size={32} />
                </div>
              )}
            </div>
            <h2 className="section-title-modern">
              <Package size={20} />
              Informações Gerais do Produto
            </h2>
          </div>

          <div className="form-grid-modern">
            <div className="form-field-modern">
              <label>SKU</label>
              {editing && canEdit ? (
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                  className={errors.sku ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">{product.sku || '-'}</div>
              )}
              {errors.sku && <span className="error-message">{errors.sku}</span>}
            </div>

            <div className="form-field-modern">
              <label>Referências (cProd)</label>
              {editing && canEdit ? (
                <>
                  <textarea
                    value={formData.referencesText}
                    onChange={(e) => setFormData({ ...formData, referencesText: e.target.value })}
                    className={errors.references ? 'error' : ''}
                    placeholder="Ex.: 2704 (um por linha)"
                    rows={2}
                  />
                  <span className="field-hint-modern">Código do fornecedor na NF-e</span>
                </>
              ) : product.references && product.references.length > 0 ? (
                <div className="field-value-modern">
                  <div className="references-list">
                    {product.references.map((ref, index) => (
                      <span key={index} className="reference-badge">
                        {ref}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="field-value-modern field-value-muted">
                  Nenhuma referência cadastrada
                </div>
              )}
              {errors.references && <span className="error-message">{errors.references}</span>}
            </div>

            <div className="form-field-modern">
              <label>Produto</label>
              {editing && canEdit ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">{product.name}</div>
              )}
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-field-modern">
              <label>Situação</label>
              {editing && canEdit ? (
                <select
                  value={formData.active ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              ) : (
                <button className={`status-badge-modern ${product.active ? 'active' : 'inactive'}`}>
                  {product.active ? 'Ativo' : 'Inativo'}
                </button>
              )}
            </div>

            <div className="form-field-modern">
              <label>Destaque na Landing Page</label>
              <button
                className={`toggle-featured-modern ${product.is_featured ? 'active' : ''}`}
                onClick={async () => {
                  try {
                    const response = await adminService.toggleFeaturedProduct(product.id);
                    if (response.success) {
                      setProduct({ ...product, is_featured: !product.is_featured });
                    }
                  } catch (error) {
                    console.error('Erro ao atualizar destaque:', error);
                  }
                }}
                disabled={!canEdit}
                title={product.is_featured ? 'Remover dos destaques' : 'Adicionar aos destaques'}
              >
                <span className="toggle-icon">
                  {product.is_featured ? '⭐' : '☆'}
                </span>
                <span className="toggle-text">
                  {product.is_featured ? 'Em Destaque' : 'Sem Destaque'}
                </span>
              </button>
            </div>

            <div className="form-field-modern">
              <label>Grupo</label>
              {editing && canEditGroup ? (
                <button
                  type="button"
                  className={`lookup-trigger-modern${formData.groupId ? ' has-value' : ''}`}
                  onClick={() => openLookupModal('group')}
                >
                  <Layers size={16} className="lookup-trigger-icon" />
                  <span className="lookup-trigger-text">{selectedGroupLabel}</span>
                  <ChevronDown size={16} />
                </button>
              ) : (
                <>
                  <div className="field-value-modern">{product.group_name || '-'}</div>
                  {editing && !canEditGroup ? (
                    <p className="tab-description-text-modern text-muted">
                      Seu grupo de acesso não pode editar o grupo.
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <div className="form-field-modern">
              <label>Marca</label>
              {editing && canEditBrand ? (
                <button
                  type="button"
                  className={`lookup-trigger-modern${formData.brandId ? ' has-value' : ''}`}
                  onClick={() => openLookupModal('brand')}
                >
                  <Tag size={16} className="lookup-trigger-icon" />
                  <span className="lookup-trigger-text">{selectedBrandLabel}</span>
                  <ChevronDown size={16} />
                </button>
              ) : (
                <>
                  <div className="field-value-modern">{product.brand_name || '-'}</div>
                  {editing && !canEditBrand ? (
                    <p className="tab-description-text-modern text-muted">
                      Seu grupo de acesso não pode editar a marca.
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <div className="form-field-modern">
              <label>Estoque Atual</label>
              <div className="field-value-modern">
                <span className={`stock-badge-modern ${(product.stock_quantity || 0) > 0 ? 'in-stock' : 'out-of-stock'}`}>
                  {product.stock_quantity || 0}
                </span>
              </div>
            </div>

            <div className="form-field-modern">
              <label>Estoque Mínimo</label>
              {editing && canEdit ? (
                <input
                  type="number"
                  min="0"
                  value={formData.minStock}
                  onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
                />
              ) : (
                <div className="field-value-modern">{product.min_stock || 0}</div>
              )}
            </div>

          </div>
        </div>

        {/* Card de Preço com Gradiente Verde */}
        <div className="price-card-gradient-modern">
          <div className="price-card-gradient-overlay-1"></div>
          <div className="price-card-gradient-overlay-2"></div>
          <div className="price-card-content-modern">
            <div className="price-card-header-modern">
              <span className="price-card-label-modern">Preço de Venda</span>
            </div>
            {editing && canEdit ? (
              <div className="price-card-edit-modern">
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className={`price-card-input-modern ${errors.price ? 'error' : ''}`}
                  placeholder="0.00"
                />
                {errors.price && <span className="error-message">{errors.price}</span>}
              </div>
            ) : (
              <div className="price-card-value-modern">
                <span className="price-card-currency-modern">R$</span>
                <span className="price-card-amount-modern">{formatCurrency(product.price).replace('R$', '').trim()}</span>
              </div>
            )}
            {!editing && product.cost_price && (
              <div className="price-card-meta-modern">
                <span className="price-card-old-price-modern">
                  De {formatCurrency(product.price * 1.25)}
                </span>
                <span className="price-card-installment-modern">
                  Ou 10x de {formatCurrency(product.price / 10)} sem juros
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Seção: Informações Financeiras com Gradiente */}
        <div className="detail-section-modern detail-section-gradient-2">
          <h2 className="section-title-modern">
            <DollarSign size={20} />
            Informações Financeiras
          </h2>

          <div className="form-grid-modern">
            {hideProductCost ? null : (
            <div className="form-field-modern">
              <label>Custo</label>
              {editing && canEdit ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.costPrice}
                  onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                  className={errors.costPrice ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">{formatCurrency(product.cost_price)}</div>
              )}
              {errors.costPrice && <span className="error-message">{errors.costPrice}</span>}
            </div>
            )}

            {hideProductCost ? null : (
            <div className="form-field-modern calculated-field">
              <label>Markup</label>
              <div className="field-value-modern calculated">
                <Percent size={16} />
                {formatPercent(calculations.markup)}
              </div>
            </div>
            )}

            {hideProductCost ? null : (
            <div className="form-field-modern calculated-field">
              <label>Margem</label>
              <div className="field-value-modern calculated">
                <Percent size={16} />
                {formatPercent(calculations.margin)}
              </div>
            </div>
            )}

            <div className="form-field-modern">
              <label>Preço mínimo</label>
              {editing && canEdit ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.minPrice}
                  onChange={(e) => setFormData({ ...formData, minPrice: e.target.value })}
                  className={errors.minPrice ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">{formatCurrency(calculations.minPrice)}</div>
              )}
              {errors.minPrice && <span className="error-message">{errors.minPrice}</span>}
            </div>

            {hideProductCost ? null : (
            <div className="form-field-modern calculated-field">
              <label>Markup mínimo</label>
              <div className="field-value-modern calculated">
                <Percent size={16} />
                {formatPercent(calculations.minMarkup)}
              </div>
            </div>
            )}

            {hideProductCost ? null : (
            <div className="form-field-modern calculated-field">
              <label>Margem mínima</label>
              <div className="field-value-modern calculated">
                <Percent size={16} />
                {formatPercent(calculations.minMargin)}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Seção: Informações Adicionais */}
        <div className="detail-section-modern detail-section-gradient-3">
          <h2 className="section-title-modern">
            <Package size={20} />
            Informações Adicionais
          </h2>

          <div className="form-grid-modern">
            <div className="form-field-modern">
              <label>Unidade principal</label>
              {editing && canEdit ? (
                <button
                  type="button"
                  className={`lookup-trigger-modern${formData.unitId ? ' has-value' : ''}`}
                  onClick={() => openLookupModal('unit')}
                >
                  <Ruler size={16} className="lookup-trigger-icon" />
                  <span className="lookup-trigger-text">{selectedUnitLabel}</span>
                  <ChevronDown size={16} />
                </button>
              ) : (
                <div className="field-value-modern">
                  {product.unit_name ? `${product.unit_name} - ${product.unit_code || ''}` : '-'}
                </div>
              )}
            </div>

            <div className="form-field-modern barcode-field">
              <label>Código de barras</label>
              {editing && canEdit ? (
                <div className="barcode-input-group-modern">
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    placeholder="7898969193351"
                    className={errors.barcode ? 'error' : ''}
                  />
                  <button
                    type="button"
                    className="btn-generate-barcode-modern"
                    onClick={generateBarcode}
                    title="Gerar código de barras automaticamente"
                  >
                    <Barcode size={18} />
                    Gerar
                  </button>
                </div>
              ) : (
                <div className="field-value-modern">{product.barcode || '-'}</div>
              )}
              {errors.barcode && <span className="error-message">{errors.barcode}</span>}
            </div>
          </div>
        </div>

        {/* Seção: Cubagem e Frete */}
        <div className="detail-section-modern detail-section-gradient-4">
          <h2 className="section-title-modern">
            <Ruler size={20} />
            Cubagem e Frete
          </h2>
          {editing && !canEditCubage ? (
            <p className="tab-description-text-modern text-muted">
              Seu grupo de acesso não pode editar cubagem e frete.
            </p>
          ) : null}

          <div className="form-grid-modern">
            <div className="form-field-modern">
              <label>Largura (cm)</label>
              {editing && canEditCubage ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                  placeholder="0.00"
                  className={errors.width ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">
                  {product.width ? `${product.width} cm` : '-'}
                </div>
              )}
              {errors.width && <span className="error-message">{errors.width}</span>}
            </div>

            <div className="form-field-modern">
              <label>Altura (cm)</label>
              {editing && canEditCubage ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="0.00"
                  className={errors.height ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">
                  {product.height ? `${product.height} cm` : '-'}
                </div>
              )}
              {errors.height && <span className="error-message">{errors.height}</span>}
            </div>

            <div className="form-field-modern">
              <label>Comprimento (cm)</label>
              {editing && canEditCubage ? (
                <input
                  type="number"
                  step="0.01"
                  value={formData.length}
                  onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                  placeholder="0.00"
                  className={errors.length ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">
                  {product.length ? `${product.length} cm` : '-'}
                </div>
              )}
              {errors.length && <span className="error-message">{errors.length}</span>}
            </div>

            <div className="form-field-modern">
              <label>Peso (kg)</label>
              {editing && canEditCubage ? (
                <input
                  type="number"
                  step="0.001"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="0.000"
                  className={errors.weight ? 'error' : ''}
                />
              ) : (
                <div className="field-value-modern">
                  {product.weight ? `${product.weight} kg` : '-'}
                </div>
              )}
              {errors.weight && <span className="error-message">{errors.weight}</span>}
            </div>
          </div>

          {product.width && product.height && product.length && product.weight && (
            <div className="cubage-info-modern">
              <div className="cubage-item-modern">
                <span className="cubage-label">Volume:</span>
                <span className="cubage-value">
                  {((product.width * product.height * product.length) / 1000000).toFixed(4)} m³
                </span>
              </div>
              <div className="cubage-item-modern">
                <span className="cubage-label">Peso Cúbico:</span>
                <span className="cubage-value">
                  {(((product.width * product.height * product.length) / 6000) * product.weight).toFixed(2)} kg
                </span>
              </div>
            </div>
          )}
        </div>
          </>
        ) : null}

        {activeTab === 'fotos' ? (
        /* Seção: Imagens */
        <div className="detail-section-modern detail-section-gradient-5">
          <h2 className="section-title-modern">
            <ImageIcon size={20} />
            Imagens do Produto
          </h2>
          <ProductImageManager 
            productId={parseInt(id!)}
            productLabel={product.sku || product.name}
            editable={canManageImages}
            onImagesChange={async () => {
              try {
                const imagesResponse = await adminService.getProductImages(id!);
                if (imagesResponse.success && imagesResponse.data) {
                  const formattedImages = imagesResponse.data.map((img: any) => ({
                    id: img.id,
                    url: img.image_url,
                    isPrimary: img.display_order === 0
                  }));
                  setImages(formattedImages);
                  if (formattedImages.length > 0 && selectedImageIndex >= formattedImages.length) {
                    setSelectedImageIndex(0);
                  }
                }
              } catch (error) {
                console.error('Erro ao recarregar imagens:', error);
              }
            }}
          />
        </div>
        ) : null}

        {activeTab === 'grade' && canEdit ? (
          <>
        {/* Seção: Grade */}
          <ProductGridManager
            productId={product.id}
            currentGridId={product.grid_id}
            onGridChange={(gridId) => {
              setProduct({ ...product, grid_id: gridId });
            }}
          />

        {/* Seção: Variações */}
        {product.grid_id && (
          <ProductVariantsManager productId={product.id} />
        )}
          </>
        ) : null}

        {activeTab === 'descricao' ? (
        /* Seção: Descrição e Especificações com Tabs */
        <div className="tabs-section-modern">
          <div className="tabs-header-modern">
            <button 
              onClick={() => setContentSubTab('descricao')}
              className={`tab-button-modern ${contentSubTab === 'descricao' ? 'active' : ''}`}
            >
              Descrição Detalhada
              {contentSubTab === 'descricao' && <div className="tab-indicator-modern" />}
            </button>
            <button 
              onClick={() => setContentSubTab('especificacoes')}
              className={`tab-button-modern ${contentSubTab === 'especificacoes' ? 'active' : ''}`}
            >
              Especificações Técnicas
              {contentSubTab === 'especificacoes' && <div className="tab-indicator-modern" />}
            </button>
          </div>

          <div className="tabs-content-modern">
            {contentSubTab === 'descricao' ? (
              <div className="tab-content-inner-modern">
                <div className="tab-header-info-modern">
                  <div className="tab-icon-wrapper-modern">
                    <Info size={32} />
                  </div>
                  <h3 className="tab-title-modern">Sobre o Produto</h3>
                </div>
                <div className="tab-description-modern">
                  {canEditDescription ? (
                    <>
                      <textarea
                        rows={8}
                        value={formData.ecommerceDescription}
                        onChange={(e) => setFormData({ ...formData, ecommerceDescription: e.target.value })}
                        placeholder="Digite a descrição do produto que será exibida no e-commerce..."
                        className={`tab-textarea-modern ${errors.ecommerceDescription ? 'error' : ''}`}
                      />
                      {!editing && (
                        <div className="tab-description-actions">
                          <button
                            type="button"
                            className="btn-primary-modern tab-description-save"
                            onClick={handleSaveDescription}
                            disabled={!descriptionDirty || descriptionSaving}
                          >
                            <Save size={16} />
                            {descriptionSaving ? 'Salvando...' : 'Salvar descrição'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {product.ecommerce_description ? (
                        <p className="tab-description-text-modern">
                          {product.ecommerce_description}
                        </p>
                      ) : (
                        <p className="tab-description-text-modern text-muted">
                          Sem descrição disponível.
                        </p>
                      )}
                      {editing && !canEditDescription && (
                        <p className="tab-description-text-modern text-muted">
                          Seu grupo de acesso não pode editar a descrição.
                        </p>
                      )}
                      {product.description && product.description !== product.ecommerce_description && (
                        <p className="tab-description-text-modern">
                          {product.description}
                        </p>
                      )}
                    </>
                  )}
                  {errors.ecommerceDescription && <span className="error-message">{errors.ecommerceDescription}</span>}
                </div>
                {!editing && (
                  <div className="tab-features-grid-modern">
                    <div className="tab-feature-card-modern tab-feature-primary-modern">
                      <div className="tab-feature-icon-modern">
                        <Zap size={24} />
                      </div>
                      <div>
                        <h4 className="tab-feature-title-modern">Performance</h4>
                        <p className="tab-feature-text-modern">
                          Tecnologia de última geração para resultados precisos em avaliações clínicas de alta complexidade.
                        </p>
                      </div>
                    </div>
                    <div className="tab-feature-card-modern tab-feature-secondary-modern">
                      <div className="tab-feature-icon-modern">
                        <ShieldCheck size={24} />
                      </div>
                      <div>
                        <h4 className="tab-feature-title-modern">Confiabilidade</h4>
                        <p className="tab-feature-text-modern">
                          Materiais certificados de alta resistência para uso intensivo e esterilização frequente.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="tab-content-inner-modern">
                <div className="tab-specs-grid-modern">
                  {[
                    { label: 'Marca', value: product.brand_name || product.brand || '-' },
                    { label: 'SKU', value: product.sku || '-' },
                    { label: 'Código de Barras', value: product.barcode || '-' },
                    { label: 'Peso', value: product.weight ? `${product.weight} kg` : '-' },
                    { label: 'Dimensões (LxAxP)', value: product.width && product.height && product.length ? `${product.width} x ${product.height} x ${product.length} cm` : '-' },
                    { label: 'Unidade', value: product.unit_name ? `${product.unit_name} - ${product.unit_code || ''}` : '-' },
                    { label: 'Grupo', value: product.group_name || '-' },
                    { label: 'Estoque', value: `${product.stock_quantity || 0} unidades` },
                  ].map((spec, i) => (
                    <div key={i} className="tab-spec-item-modern">
                      <span className="tab-spec-label-modern">{spec.label}</span>
                      <span className="tab-spec-value-modern">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        ) : null}

        {activeTab === 'historico' ? (
        /* Seção: Histórico */
        <div className="detail-section-modern detail-section-gradient-6">
          <h2 className="section-title-modern">
            <History size={20} />
            Histórico
          </h2>

          <div className="history-buttons-modern">
            <button
              className="history-btn-modern"
              onClick={() => setHistoryModals({ ...historyModals, prices: true })}
            >
              <TrendingUp size={18} />
              Histórico de Preços
            </button>
            <button
              className="history-btn-modern"
              onClick={() => setHistoryModals({ ...historyModals, sales: true })}
            >
              <ShoppingCart size={18} />
              Histórico de Vendas
            </button>
            <button
              className="history-btn-modern"
              onClick={() => setHistoryModals({ ...historyModals, quantities: true })}
            >
              <Package2 size={18} />
              Histórico de Quantidades
            </button>
            <button
              className="history-btn-modern"
              onClick={() => setHistoryModals({ ...historyModals, purchases: true })}
            >
              <ShoppingBag size={18} />
              Histórico de Compras
            </button>
          </div>
        </div>
        ) : null}

        {errors.submit && (
          <div className="form-error-message-modern">{errors.submit}</div>
        )}
      </div>

      {/* Modal de Zoom de Imagem */}
      <Modal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        title="Imagem do Produto"
        size="large"
        footer={null}
      >
        <div className="image-modal-content-modern">
          {currentImage && (
            <div className="modal-image-wrapper">
              <img 
                src={currentImage} 
                alt={product.name}
                className="modal-zoomed-image-modern"
              />
            </div>
          )}
          {images.length > 1 && (
            <div className="modal-thumbnails-modern">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`modal-thumbnail-modern ${selectedImageIndex === idx ? 'active' : ''}`}
                >
                  <img src={img.url} alt={`${product.name} ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Modais de Histórico */}
      <Modal
        isOpen={historyModals.prices}
        onClose={() => setHistoryModals({ ...historyModals, prices: false })}
        title="Histórico de Preços"
        size="large"
        footer={null}
      >
        <div className="history-content-modern">
          {historyData.prices.loading ? (
            <div className="history-loading">Carregando histórico...</div>
          ) : historyData.prices.error ? (
            <div className="history-error">{historyData.prices.error}</div>
          ) : historyData.prices.data.length === 0 ? (
            <p className="history-empty">Nenhum registro de histórico de preços encontrado.</p>
          ) : (
            <div className="history-table-container-modern">
              <table className="history-table-modern">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Preço Anterior</th>
                    <th>Preço Novo</th>
                    {hideProductCost ? null : <th>Custo Anterior</th>}
                    {hideProductCost ? null : <th>Custo Novo</th>}
                    <th>Alterado por</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.prices.data.map((item: any) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.created_at)}</td>
                      <td>{item.old_price ? formatCurrency(item.old_price) : '-'}</td>
                      <td>{formatCurrency(item.new_price)}</td>
                      {hideProductCost ? null : (
                        <td>{item.old_cost_price ? formatCurrency(item.old_cost_price) : '-'}</td>
                      )}
                      {hideProductCost ? null : (
                        <td>{item.new_cost_price ? formatCurrency(item.new_cost_price) : '-'}</td>
                      )}
                      <td>{item.changed_by_name || item.changed_by_email || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={historyModals.sales}
        onClose={() => setHistoryModals({ ...historyModals, sales: false })}
        title="Histórico de Vendas"
        size="xlarge"
        footer={null}
      >
        <div className="history-content-modern">
          {historyData.sales.loading ? (
            <div className="history-loading">Carregando histórico...</div>
          ) : historyData.sales.error ? (
            <div className="history-error">{historyData.sales.error}</div>
          ) : (
            <>
              {historyData.sales.statistics && (
                <div className="history-statistics-modern">
                  <div className="stat-item-modern">
                    <span className="stat-label">Total de Vendas:</span>
                    <span className="stat-value">{historyData.sales.statistics.total_sales || 0}</span>
                  </div>
                  <div className="stat-item-modern">
                    <span className="stat-label">Quantidade Total:</span>
                    <span className="stat-value">{historyData.sales.statistics.total_quantity || 0}</span>
                  </div>
                  <div className="stat-item-modern">
                    <span className="stat-label">Receita Total:</span>
                    <span className="stat-value">{formatCurrency(historyData.sales.statistics.total_revenue || 0)}</span>
                  </div>
                  <div className="stat-item-modern">
                    <span className="stat-label">Preço Médio:</span>
                    <span className="stat-value">{formatCurrency(historyData.sales.statistics.average_price || 0)}</span>
                  </div>
                </div>
              )}
              {historyData.sales.data.length === 0 ? (
                <p className="history-empty">Nenhum registro de histórico de vendas encontrado.</p>
              ) : (
                <div className="history-table-container-modern">
                  <table className="history-table-modern">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Venda #</th>
                        <th>Quantidade</th>
                        <th>Preço Unit.</th>
                        <th>Total</th>
                        <th>Cliente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.sales.data.map((item: any) => (
                        <tr key={item.id}>
                          <td>{formatDate(item.sale_date)}</td>
                          <td>{item.sale_id || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.unit_price)}</td>
                          <td>{formatCurrency(item.total_price)}</td>
                          <td>{item.customer_name || item.customer_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={historyModals.quantities}
        onClose={() => setHistoryModals({ ...historyModals, quantities: false })}
        title="Histórico de Quantidades"
        size="xlarge"
        footer={null}
      >
        <div className="history-content-modern">
          {historyData.quantities.loading ? (
            <div className="history-loading">Carregando histórico...</div>
          ) : historyData.quantities.error ? (
            <div className="history-error">{historyData.quantities.error}</div>
          ) : (
            <>
              {historyData.quantities.data.length === 0 ? (
                <p className="history-empty">Nenhum registro de histórico de quantidades encontrado.</p>
              ) : (
                <div className="history-table-container-modern">
                  <table className="history-table-modern">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>Data</th>
                        <th>Origem</th>
                        <th>Quantidade de Movimento</th>
                        <th>Total de Estoque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.quantities.data.map((item: any) => {
                        const variation = item.new_quantity - item.old_quantity;
                        const movementOrigin = item.movement_origin || item.type_display || item.movement_type || 'Ajuste';
                        return (
                          <tr key={item.id}>
                            <td>{item.changed_by_name || item.changed_by_email || '-'}</td>
                            <td>{formatDate(item.created_at)}</td>
                            <td>
                              <span className={`movement-type-modern ${item.movement_type?.toLowerCase() || 'ajuste'}`}>
                                {movementOrigin}
                              </span>
                            </td>
                            <td className={variation > 0 ? 'positive' : variation < 0 ? 'negative' : ''}>
                              {variation > 0 ? '+' : ''}{variation}
                            </td>
                            <td>{item.new_quantity}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={historyModals.purchases}
        onClose={() => setHistoryModals({ ...historyModals, purchases: false })}
        title="Histórico de Compras"
        size="xlarge"
        footer={null}
      >
        <div className="history-content-modern">
          {historyData.purchases.loading ? (
            <div className="history-loading">Carregando histórico...</div>
          ) : historyData.purchases.error ? (
            <div className="history-error">{historyData.purchases.error}</div>
          ) : (
            <>
              {historyData.purchases.statistics && (
                <div className="history-statistics-modern">
                  <div className="stat-item-modern">
                    <span className="stat-label">Total de Compras:</span>
                    <span className="stat-value">{historyData.purchases.statistics.total_purchases || 0}</span>
                  </div>
                  <div className="stat-item-modern">
                    <span className="stat-label">Quantidade Total:</span>
                    <span className="stat-value">{historyData.purchases.statistics.total_quantity || 0}</span>
                  </div>
                  {hideProductCost ? null : (
                  <div className="stat-item-modern">
                    <span className="stat-label">Custo Total:</span>
                    <span className="stat-value">{formatCurrency(historyData.purchases.statistics.total_cost || 0)}</span>
                  </div>
                  )}
                  {hideProductCost ? null : (
                  <div className="stat-item-modern">
                    <span className="stat-label">Custo Médio:</span>
                    <span className="stat-value">{formatCurrency(historyData.purchases.statistics.average_cost || 0)}</span>
                  </div>
                  )}
                </div>
              )}
              {historyData.purchases.data.length === 0 ? (
                <p className="history-empty">Nenhum registro de histórico de compras encontrado.</p>
              ) : (
                <div className="history-table-container-modern">
                  <table className="history-table-modern">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Compra #</th>
                        <th>Fornecedor</th>
                        <th>Quantidade</th>
                        {hideProductCost ? null : <th>Custo Unit.</th>}
                        {hideProductCost ? null : <th>Total</th>}
                        <th>Nota Fiscal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.purchases.data.map((item: any) => (
                        <tr key={item.id}>
                          <td>{formatDate(item.purchase_date)}</td>
                          <td>{item.purchase_id || '-'}</td>
                          <td>{item.supplier_name || item.supplier_id || '-'}</td>
                          <td>{item.quantity}</td>
                          {hideProductCost ? null : <td>{formatCurrency(item.unit_cost)}</td>}
                          {hideProductCost ? null : <td>{formatCurrency(item.total_cost)}</td>}
                          <td>{item.invoice_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <SearchSelectModal
        isOpen={lookupModal === 'group'}
        onClose={closeLookupModal}
        title="Selecionar Grupo"
        subtitle="Busque e escolha o grupo do produto"
        icon={<Layers size={24} />}
        items={groupItems}
        selectedId={formData.groupId || null}
        searchTerm={lookupSearchTerm}
        onSearchChange={setLookupSearchTerm}
        searchPlaceholder="Buscar grupo..."
        emptyLabel="Nenhum grupo disponível"
        emptySearchLabel="Nenhum grupo encontrado"
        clearLabel="Remover grupo"
        onSelect={(item) => {
          setFormData((prev) => ({ ...prev, groupId: String(item.id) }));
          closeLookupModal();
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, groupId: '' }));
          closeLookupModal();
        }}
      />

      <SearchSelectModal
        isOpen={lookupModal === 'brand'}
        onClose={closeLookupModal}
        title="Selecionar Marca"
        subtitle="Busque e escolha a marca do produto"
        icon={<Tag size={24} />}
        items={brandItems}
        selectedId={formData.brandId || null}
        searchTerm={lookupSearchTerm}
        onSearchChange={setLookupSearchTerm}
        searchPlaceholder="Buscar marca..."
        emptyLabel="Nenhuma marca disponível"
        emptySearchLabel="Nenhuma marca encontrada"
        clearLabel="Remover marca"
        onSelect={(item) => {
          setFormData((prev) => ({ ...prev, brandId: String(item.id) }));
          closeLookupModal();
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, brandId: '' }));
          closeLookupModal();
        }}
      />

      <SearchSelectModal
        isOpen={lookupModal === 'unit'}
        onClose={closeLookupModal}
        title="Selecionar Unidade de Medida"
        subtitle="Busque e escolha a unidade principal"
        icon={<Ruler size={24} />}
        items={unitItems}
        selectedId={formData.unitId || null}
        searchTerm={lookupSearchTerm}
        onSearchChange={setLookupSearchTerm}
        searchPlaceholder="Buscar por nome ou código..."
        emptyLabel="Nenhuma unidade disponível"
        emptySearchLabel="Nenhuma unidade encontrada"
        clearLabel="Remover unidade"
        onSelect={(item) => {
          setFormData((prev) => ({ ...prev, unitId: String(item.id) }));
          closeLookupModal();
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, unitId: '' }));
          closeLookupModal();
        }}
      />
    </div>
  );
};

export default ProductDetailPage;
